import React, {useEffect, useState} from "react";
import {Route, Switch, useHistory, useLocation} from "react-router-dom";
import CIRepResultsTable from "../CIRepResultsTable/CIRepResultsTable";
import {CanonicalRepresentationRequestInputData} from "src/model/CanonicalRepresentationRequestInputData";
import {CanonicalRepresentationOutputItem} from "src/model/CanonicalRepresentationOutputItem";
import {REGION_HARDWARE_SOURCE_MAP} from "../CIRepSearchForm/constant";
import {getCanonicalRepresentation} from "src/api/Api";
import CIRepSearchForm from "../CIRepSearchForm/CIRepSearchForm";
import {generateCanonicalRepresentationRequestBody} from "./CIRepApiInputTransformation";
import GetCanonicalRepresentationRequest from "src/model/GetCanonicalRepresentationRequest";
import CIRepImageViewer from "../CIRepImageViewer/CIRepImageViewer";

/**
 * This container is used for the high level layout components on CanonicalImages page.
 */

const CanonicalImages: React.FunctionComponent = () => {
    // Making the initial state of CIRepRequestInputData to be undefined instead of empty list to be able to
    // distinguish between initial state and empty list returned by API when there are no captures for item
    const [canonicalRepresentationData, setCanonicalRepresentationData] = useState<Array<CanonicalRepresentationOutputItem>>();
    const [canonicalRepresentationToDisplay, setCanonicalRepresentationToDisplay] = useState<CanonicalRepresentationOutputItem | undefined>();
    const [displayResultsTableProgressBar, setDisplayResultsTableProgressBar] = useState<boolean>(false);
    const [displayResultsTableErrorMessage, setDisplayResultsTableErrorMessage] = useState<boolean>(false);
    const [inputData, setInputData] = useState<CanonicalRepresentationRequestInputData>();


    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const searchQuery = new URLSearchParams(location.search);

        // use the inbuilt methods to get the value from searchQuery
        const itemId = searchQuery.get("itemId")
        const itemIdType = searchQuery.get("itemIdType")
        const hardwareSource = searchQuery.get("hardwareSource")
        let affinityThreshold = searchQuery.get("affinityThreshold")
        let maxResults = searchQuery.get("maxResults")
        const region =  searchQuery.get("region")

        // update state and fetch data only when all query parameter provided
        if (itemId && itemIdType && hardwareSource && affinityThreshold && maxResults && region) {
            if(hardwareSource === "ALL") {
                maxResults = "";
                affinityThreshold = "ALL";
            }
            let inputDataParams = {
                itemId: itemId,
                itemIdType: itemIdType,
                hardwareSource: hardwareSource,
                affinityThreshold: affinityThreshold,
                maxResults: parseInt(maxResults),
                region: region
            }
            setInputData(inputDataParams);

            const fetchData = async () => {
                setDisplayResultsTableProgressBar(true);
                try {
                    let canonicalRepresentationResult: CanonicalRepresentationOutputItem[] = [];
                    await fetchCanonicalRepresentation(inputDataParams, canonicalRepresentationResult);
                    setCanonicalRepresentationData(canonicalRepresentationResult);
                    setDisplayResultsTableErrorMessage(false);
                }
                catch (e) {
                    setCanonicalRepresentationData(undefined);
                    setDisplayResultsTableErrorMessage(true);
                }
                setDisplayResultsTableProgressBar(false);
            };
            fetchData();
        }
    },[])

    const getCanonicalRepresentationFromAPI = async (hardwareSource: string,
                                                     region: string,
                                                     requestBody: GetCanonicalRepresentationRequest) => {
        const apiRequestBodies = generateCanonicalRepresentationRequestBody(requestBody);
        let responseList: Array<any> = [];
        for (let requestBody of apiRequestBodies) {
            let responseData = await getCanonicalRepresentation(region, requestBody);
            responseList = responseList.concat(responseData.results)
        }

        return responseList
            .map((result: any) => (
                {
                    hardwareSource: hardwareSource,
                    uuid: hardwareSource + "_" + result.itemId + "_" + result.affinityThreshold,
                    ...result
                }
            ));
    }

    const fetchCanonicalRepresentation = async (inputData: CanonicalRepresentationRequestInputData, ciRepResult: CanonicalRepresentationOutputItem[]) => {
        const {region, ...getCanonicalRepresentationRequestBody} = inputData;

        if (inputData.hardwareSource === "ALL") {
            const regionTyped = inputData.region as keyof typeof REGION_HARDWARE_SOURCE_MAP;
            let hardwareSourceArray =  REGION_HARDWARE_SOURCE_MAP[regionTyped];
            for ( let i = 1; i < hardwareSourceArray.length; i++ ) {
                const requestData = Object.assign({}, getCanonicalRepresentationRequestBody,
                    {hardwareSource: hardwareSourceArray[i].value});
                const canonicalRepresentationWithHardwareSource =
                    await getCanonicalRepresentationFromAPI(hardwareSourceArray[i].value, inputData.region, requestData)
                ciRepResult.push(...canonicalRepresentationWithHardwareSource);
            }
        } else {
            const canonicalRepresentationWithHardwareSource =
                await getCanonicalRepresentationFromAPI(inputData.hardwareSource, inputData.region, getCanonicalRepresentationRequestBody)
            ciRepResult.push(...canonicalRepresentationWithHardwareSource);
        }
    };

    const onSearch = async (inputData: CanonicalRepresentationRequestInputData) => {
        let path = "/canonicalImages/search?region=" +
            inputData.region +
            "&hardwareSource=" +
            inputData.hardwareSource +
            "&affinityThreshold=" +
            inputData.affinityThreshold +
            "&maxResults=" +
            inputData.maxResults +
            "&itemIdType=" +
            inputData.itemIdType +
            "&itemId=" +
            inputData.itemId;

        history.push(path);

        setDisplayResultsTableProgressBar(true);
        try {
            let canonicalRepresentationResult: CanonicalRepresentationOutputItem[] = [];
            await fetchCanonicalRepresentation(inputData, canonicalRepresentationResult);
            setCanonicalRepresentationData(canonicalRepresentationResult);
            setDisplayResultsTableErrorMessage(false);
        }
        catch (e) {
            setCanonicalRepresentationData(undefined);
            setDisplayResultsTableErrorMessage(true);
        }
        setInputData(inputData);
        setDisplayResultsTableProgressBar(false);
        setCanonicalRepresentationToDisplay(undefined);
    };

    return (
        <>
            <CIRepSearchForm initialInputData={inputData!} onSubmit={onSearch} />
            <Switch>
                <Route path="/canonicalImages/search">
                    <CIRepResultsTable
                            itemCanonicalRepresentationData={canonicalRepresentationData}
                            inputData={inputData!}
                            displayProgressBar={displayResultsTableProgressBar}
                            displayResultsTableErrorMessage={displayResultsTableErrorMessage}
                            setCanonicalRepresentationToDisplay={setCanonicalRepresentationToDisplay}
                            setDisplayProgressBar={setDisplayResultsTableProgressBar}
                    />
                    <CIRepImageViewer
                            canonicalRepresentationToDisplay={canonicalRepresentationToDisplay}
                            inputData={inputData!}
                    />
                </Route>
            </Switch>
        </>
    );

}

export default CanonicalImages;
